import { indexArray } from '../utils'
import { getDatadogRum } from '../externals'
import type { AppManifest, AppManifests, FeatureFlag, FeatureFlags, ReadonlyFeatureFlags } from '../stateTypes'

export { indexTsgTree } from './indexTsgTree'
export { indexTsgInstances } from './indexTsgInstances'
export { indexSupportAccounts } from './indexSupportAccounts'
export type { ReadonlyTsgTree, TsgTree, Tsg } from './indexTsgTree'
export type { ReadonlyTsgInstances, TsgInstances, Instance, Association, TsgInstanceError } from './indexTsgInstances'

export const indexAppManifests = (apps: AppManifest[] | undefined): AppManifests => {
  if (!apps || (Array.isArray(apps) && apps.length === 0)) {
    const indexedApps = indexArray<AppManifest>([], 'app_id')
    Object.defineProperties(indexedApps, {
      getDisplayName: {
        value: (_appId: string | undefined) => undefined
      }
    })
    return indexedApps as unknown as AppManifests
  }
  if (!Array.isArray(apps)) {
    return indexAppManifests.fromError(new Error('apps is not an array'))
  }
  const indexedApps = indexArray<AppManifest>(apps, 'app_id')
  Object.defineProperties(indexedApps, {
    getDisplayName: {
      value: (appId: string | undefined) => (
        (appId && indexedApps.get(appId)?.display_name) || undefined
      )
    }
  })
  return indexedApps as unknown as AppManifests
}
indexAppManifests.fromError = (error: Error) => {
  const apps = indexAppManifests(undefined)
  apps.error = error
  return apps
}

const trackFeatureFlag = (name: string | undefined, value: any): void => {
  if (name && value !== undefined) {
    requestIdleCallback(() => {
      const ffReplaceRegex = /[.:+\-=&&||><!(){}[\]^"""~*?\\]/g
      const ddRum = getDatadogRum()
      ddRum?.onReady(() => { // in case dd is not inited yet
        ddRum.addFeatureFlagEvaluation?.(name.replace(ffReplaceRegex, '_'), value)
      })
    }, { timeout: 1000 })
  }
}

const indexFeatureFlagsFn = (featureFlags: FeatureFlag[] | undefined, error?: Error): ReadonlyFeatureFlags => {
  if (!featureFlags || (Array.isArray(featureFlags) && featureFlags.length === 0)) {
    const indexedFlags = indexArray<FeatureFlag>([], 'name')
    Object.defineProperties(indexedFlags, {
      getValue: {
        value: (_name: string | undefined) => undefined
      },
      getDefaultValue: {
        value: (_name: string | undefined) => undefined
      },
    })
    if (error) {
      Object.assign(indexedFlags, { error })
    }
    return Object.freeze(indexedFlags as unknown as FeatureFlags)
  }
  if (!Array.isArray(featureFlags)) {
    return indexFeatureFlagsFn(undefined, new Error('featureFlags is not an array'))
  }
  const indexedFlags = indexArray<FeatureFlag>(featureFlags, 'name')
  Object.defineProperties(indexedFlags, {
    getValue: {
      value: <T = any>(name: string | undefined): T | undefined => {
        const value = name ? indexedFlags.get(name)?.value : undefined
        trackFeatureFlag(name, value)
        return value
      }
    },
    getDefaultValue: {
      value: <T = any>(name: string | undefined): T | undefined => (
        name ? indexedFlags.get(name)?.default_value : undefined
      )
    },
  })
  if (error) {
    Object.assign(indexedFlags, { error })
  }
  return Object.freeze(indexedFlags as unknown as FeatureFlags)
}
export const indexFeatureFlags = (featureFlags: FeatureFlag[] | undefined) => indexFeatureFlagsFn(featureFlags)
indexFeatureFlags.fromError = (error: Error) => indexFeatureFlagsFn(undefined, error)
